import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import { useStores } from '../../hooks/use-stores';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(() => ({
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        background: '#fff',
    },
    th: {
        textAlign: 'left',
        padding: 8,
        borderBottom: '1px solid #ddd',
        position: 'sticky',
        top: 0,
        background: '#fff',
        zIndex: 1,
    },
    td: {
        padding: '4px 8px',
        borderBottom: '1px solid #f0f0f0',
        fontSize: 14,
    },
    center: {
        textAlign: 'center',
    },
    hint: {
        color: '#666',
        fontSize: 14,
        maxWidth: 720,
        margin: '0 auto 16px',
        textAlign: 'left',
    },
    env: {
        color: '#8a6d3b',
        background: '#fcf8e3',
        padding: 12,
        maxWidth: 720,
        margin: '0 auto 16px',
        textAlign: 'left',
        fontSize: 14,
    },
    wrap: {
        maxWidth: 960,
        margin: '0 auto',
        maxHeight: '70vh',
        overflow: 'auto',
    },
}));

function LogSettings() {
    const classes = useStyles();
    const navigate = useNavigate();
    const { superStore } = useStores();
    const { logSettings, loadingLogSettings, savingLogSettings } = superStore;
    const [disabledLevels, setDisabledLevels] = useState({});
    const [query, setQuery] = useState('');

    useEffect(() => {
        superStore.getLogSettings();
    }, []);

    useEffect(() => {
        if (logSettings && logSettings.disabledLevels) {
            setDisabledLevels({ ...logSettings.disabledLevels });
        }
    }, [logSettings]);

    const levels = (logSettings && logSettings.levels) || [
        'error',
        'warn',
        'info',
        'verbose',
        'debug',
        'silly',
    ];
    const subsystems = (logSettings && logSettings.subsystems) || [];
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return subsystems;
        return subsystems.filter((name) => name.toLowerCase().includes(q));
    }, [subsystems, query]);

    const isEnabled = (subsystem, level) => {
        const disabled = disabledLevels[subsystem] || [];
        return !disabled.includes(level);
    };

    const toggle = (subsystem, level) => {
        setDisabledLevels((current) => {
            const next = { ...current };
            const list = new Set(next[subsystem] || []);
            if (list.has(level)) list.delete(level);
            else list.add(level);
            if (list.size === 0) delete next[subsystem];
            else next[subsystem] = [...list];
            return next;
        });
    };

    const save = async () => {
        await superStore.saveLogSettings(disabledLevels);
    };

    if (loadingLogSettings && !logSettings) return <ThemedSpinner />;

    const env = logSettings && logSettings.env;

    return (
        <Grid
            container
            direction="row"
            justify="center"
            alignItems="center"
            align="center"
            spacing={2}
        >
            <Fragment>
                <Grid item xs={12}>
                    <Typography variant="h4" gutterBottom>
                        Log Settings
                    </Typography>
                    <p className={classes.hint}>
                        Uncheck a level to stop emitting logs for that
                        subsystem. Settings are stored as{' '}
                        <code>logging.disabledLevels</code> and apply on the
                        next settings load. Error logs can be disabled, but
                        leave them on unless you are debugging volume.
                    </p>
                    {env && (env.LOG_LEVEL || env.LOG_SUBSYSTEM) && (
                        <div className={classes.env}>
                            Environment overrides are active
                            {env.LOG_LEVEL ? `: LOG_LEVEL=${env.LOG_LEVEL}` : ''}
                            {env.LOG_SUBSYSTEM
                                ? `: LOG_SUBSYSTEM=${env.LOG_SUBSYSTEM}`
                                : ''}
                            . LOG_SUBSYSTEM still allowlists subsystems before
                            these checkboxes.
                        </div>
                    )}
                </Grid>
                <Grid item xs={12}>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => navigate('/system/settings')}
                        style={{ marginRight: 12 }}
                    >
                        System Settings
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={save}
                        disabled={savingLogSettings}
                    >
                        {savingLogSettings ? 'Saving…' : 'Save log filters'}
                    </Button>
                </Grid>
                <Grid item xs={12} md={8}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        label="Filter subsystems"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                </Grid>
                <Grid item xs={12}>
                    <div className={classes.wrap}>
                        <table className={classes.table}>
                            <thead>
                                <tr>
                                    <th className={classes.th}>Subsystem</th>
                                    {levels.map((level) => (
                                        <th
                                            key={level}
                                            className={`${classes.th} ${classes.center}`}
                                        >
                                            {level}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((subsystem) => (
                                    <tr key={subsystem}>
                                        <td className={classes.td}>
                                            {subsystem}
                                        </td>
                                        {levels.map((level) => (
                                            <td
                                                key={level}
                                                className={`${classes.td} ${classes.center}`}
                                            >
                                                <Checkbox
                                                    color="primary"
                                                    checked={isEnabled(
                                                        subsystem,
                                                        level
                                                    )}
                                                    onChange={() =>
                                                        toggle(subsystem, level)
                                                    }
                                                    inputProps={{
                                                        'aria-label': `${subsystem} ${level}`,
                                                    }}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Grid>
            </Fragment>
        </Grid>
    );
}

export default observer(LogSettings);
